import {
  FC,
  FormEvent,
  KeyboardEvent,
  ReactElement,
  RefObject,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState
} from 'react';

import { useDropdownAdjustment, useOutsideClick } from 'raa-react';

import { HuvudgruppTyp } from 'models/apiModels/HuvudgruppTyp';
import { HuvudkategoriTyp } from 'models/apiModels/HuvudkategoriTyp';
import { NyckelordTyp } from 'models/apiModels/NyckelordTyp';
import { UnderkategoriTyp } from 'models/apiModels/UnderkategoriTyp';
import { AndamalTag } from 'models/appModels/AndamalTag';

import classNames from 'classnames';

// https://www.w3.org/WAI/ARIA/apg/example-index/treeview/treeview-1/treeview-1a.html
type TreeNodeType = HuvudgruppTyp | HuvudkategoriTyp | UnderkategoriTyp;

const isHuvudgruppTyp = (item: any): item is HuvudgruppTyp => item.huvudgrupptyp_id !== undefined;
const isHuvudkategoriTyp = (item: any): item is HuvudkategoriTyp => item.huvudkategorityp_id !== undefined;

enum KategoritypEnum {
  HUVUDGRUPP = 'HUVUDGRUPP',
  HUVUDKATEGORI = 'HUVUDKATEGORI',
  UNDERKATEGORI = 'UNDERKATEGORI'
}

const isDisabled = (item: TreeNodeType, disabledItems: Array<AndamalTag> | undefined): boolean => {
  if (!disabledItems) return false;
  if ('underkategorityp_id' in item) {
    return disabledItems.some(
      (el) => item.underkategorityp_id === el.id && el.kategorityp === KategoritypEnum.UNDERKATEGORI
    );
  } else if ('huvudkategorityp_id' in item) {
    return disabledItems.some(
      (el) => item.huvudkategorityp_id === el.id && el.kategorityp === KategoritypEnum.HUVUDKATEGORI
    );
  }
  return false;
};

interface OpenIndexHandlers {
  indexOpen: Array<number>;
  huvudgruppIndexOpen: Array<number>;
}

interface TreeRootProps {
  items: TreeNodeType[];
  onBlur: () => void;
  onItemSelected: (item: UnderkategoriTyp | HuvudkategoriTyp) => void;
  openIndexHandlers: OpenIndexHandlers;
  role: string;
  treeContainer: RefObject<HTMLDivElement>;
  disabledItems?: AndamalTag[];
}

const TreeRoot: FC<TreeRootProps> = ({
  role,
  items,
  openIndexHandlers,
  treeContainer,
  onBlur,
  onItemSelected,
  disabledItems
}): ReactElement => {
  return (
    <ul role={role}>
      {items.map((item: TreeNodeType, index) => (
        <TreeItem
          disabledItems={disabledItems}
          index={index}
          item={item}
          key={index}
          onBlur={onBlur}
          onItemSelected={onItemSelected}
          openIndexHandlers={openIndexHandlers}
          treeContainer={treeContainer}
        />
      ))}
    </ul>
  );
};

interface TreeItemProps {
  index: number;
  item: TreeNodeType;
  onBlur: () => void;
  onItemSelected: (item: UnderkategoriTyp | HuvudkategoriTyp) => void;
  openIndexHandlers: OpenIndexHandlers;
  treeContainer: RefObject<HTMLDivElement>;
  disabledItems?: Array<AndamalTag>;
}

const TreeItem: FC<TreeItemProps> = ({
  index,
  item,
  openIndexHandlers: { huvudgruppIndexOpen, indexOpen },
  onBlur,
  onItemSelected,
  treeContainer,
  disabledItems
}): ReactElement => {
  const anyItem = item as any;
  const items = useMemo(
    () => anyItem['huvudkategorier'] || anyItem['underkategorier'] || ([] as TreeNodeType[]),
    [anyItem]
  );

  const name = anyItem['namn'] || anyItem['definitionsnamn'] || anyItem['definition'];
  const meRef = useRef<HTMLLIElement>(null);

  const [expanded, setExpanded] = useState(false);
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    const beginsExpanded =
      items &&
      items.length > 0 &&
      ((isHuvudgruppTyp(item) && huvudgruppIndexOpen.includes(index)) ||
        (isHuvudkategoriTyp(item) && indexOpen.includes(index)));

    setExpanded(beginsExpanded);
  }, [huvudgruppIndexOpen, indexOpen, index, item, items]);

  const focusOn = (element: HTMLElement): void => {
    element.tabIndex = 0;
    element.focus({ preventScroll: true });
    (element.firstChild as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const expand = (expand: boolean) => items.length > 0 && setExpanded(expand);

  const setFocusOnParentLiItem = () => {
    const parentLiElement = meRef.current?.parentElement?.parentElement;
    if (parentLiElement && !isHuvudgruppTyp(item)) {
      focusOn(parentLiElement);
    }
  };

  const setFocusToNextItem = (): void => {
    const treeDomItems = treeContainer.current?.querySelectorAll('[role="treeitem"]');

    if (treeDomItems) {
      let nextItem: HTMLLIElement | undefined;

      for (let i = treeDomItems.length - 1; i >= 0; i--) {
        if (treeDomItems[i] === meRef.current) {
          break;
        }
        nextItem = treeDomItems[i] as HTMLLIElement;
      }

      if (nextItem && meRef.current) {
        focusOn(nextItem);
      }
    }
  };

  const setFocusToPreviousItem = (): void => {
    const treeDomItems = treeContainer.current?.querySelectorAll('[role="treeitem"]');

    if (treeDomItems) {
      let nextItem: HTMLLIElement | undefined;

      for (let i = 0; i < treeDomItems.length; i++) {
        if (treeDomItems[i] === meRef.current) {
          break;
        }
        nextItem = treeDomItems[i] as HTMLLIElement;
      }

      if (nextItem && meRef.current) {
        focusOn(nextItem);
      }
    }
  };

  const handleArrowRight = (): void => {
    if (expanded) {
      setFocusToNextItem();
    } else {
      expand(true);
    }
  };

  const handleArrowLeft = (): void => {
    if (expanded) {
      expand(false);
    } else {
      setFocusOnParentLiItem();
    }
  };

  const handleEnter = (): void => {
    if (!isHuvudgruppTyp(item) && !isDisabled(item, disabledItems)) {
      onItemSelected(item);
    } else {
      expand(!expanded);
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLLIElement>): void => {
    if (event.altKey || event.ctrlKey || event.metaKey) {
      return;
    }

    let eventCodeCapturedFlag = true;

    switch (event.code) {
      case 'ArrowDown':
        setFocusToNextItem();
        break;
      case 'ArrowUp':
        setFocusToPreviousItem();
        break;
      case 'ArrowLeft':
        handleArrowLeft();
        break;
      case 'ArrowRight':
        handleArrowRight();
        break;
      case 'Enter':
        handleEnter();
        break;
      case 'Tab':
        onBlur();
        eventCodeCapturedFlag = false;
        break;
      default:
        eventCodeCapturedFlag = false;
    }

    if (eventCodeCapturedFlag) {
      event.stopPropagation();
      event.preventDefault();
    }
  };

  const handleSelection = (): void => {
    if (isHuvudgruppTyp(item)) {
      expand(!expanded);
      return;
    }
    if (isDisabled(item, disabledItems)) return;
    onItemSelected(item);
  };

  return (
    <li
      ref={meRef}
      role="treeitem"
      aria-expanded={expanded}
      className={classNames('tw-flex tw-flex-col tw-outline-transparent', {
        'tw-ml-[20px]': !isHuvudgruppTyp(item)
      })}
      onKeyDown={handleKeyDown}
      onBlur={() => {
        if (meRef.current) {
          meRef.current.tabIndex = -1;
          setFocused(false);
        }
      }}
      onFocus={(event) => {
        event.stopPropagation();
        setFocused(true);
      }}
    >
      <div
        className={classNames('tw-box-border tw-flex', {
          'tw-bg-raa-gray-2': focused,
          'tw-bg-raa-white': !focused,
          'hover:tw-bg-raa-gray-2': isHuvudgruppTyp(item)
        })}
      >
        {((items && items.length > 0) || isHuvudgruppTyp(item)) && (
          <div
            onClick={() => expand(!expanded)}
            className={classNames(
              'tw-font-bold raa-icon-arrow-right tw-w-[40px] tw-cursor-pointer tw-text-center tw-text-size-1.625 tw-leading-[48px] hover:tw-bg-raa-gray-2',
              {
                'tw-rotate-90': expanded
              }
            )}
          />
        )}
        <div
          onClick={handleSelection}
          className={classNames('tw-grow tw-cursor-pointer tw-py-[12px] tw-pl-2 hover:tw-bg-raa-gray-2', {
            'tw-font-semibold tw-uppercase': isHuvudgruppTyp(item),
            'tw-ml-[40px]': !isHuvudgruppTyp(item) && items.length === 0,
            'tw-text-raa-gray-4': isDisabled(item, disabledItems)
          })}
        >
          {name}
        </div>
      </div>
      {expanded && (
        <TreeRoot
          disabledItems={disabledItems}
          role="group"
          items={items}
          treeContainer={treeContainer}
          onBlur={onBlur}
          onItemSelected={onItemSelected}
          openIndexHandlers={{
            indexOpen,
            huvudgruppIndexOpen
          }}
        />
      )}
    </li>
  );
};

interface CategorySelectProps {
  inputId: string;
  classNames?: string;
  disabled?: boolean;
  placeholder?: string;
  list: Array<HuvudgruppTyp> | undefined;
  onSelect: (item: UnderkategoriTyp | HuvudkategoriTyp) => void;
  tagList?: Array<TreeNodeType>;
  disabledItems?: Array<AndamalTag>;
  hasError?: boolean;
}

const CategorySelect: FC<CategorySelectProps> = (props: CategorySelectProps): ReactElement => {
  const treeContainer = useRef<HTMLDivElement>(null);
  const focusRef = useRef<HTMLDivElement>(document.createElement('div'));

  const [searchQuery, setSearchQuery] = useState<string>('');
  const [indexOpen, setIndexOpen] = useState<Array<number>>([]);
  const [dropdownExpanded, setDropdownExpanded] = useState<boolean>(false);
  const [items, setItems] = useState<Array<HuvudgruppTyp> | undefined>([]);
  const [huvudgruppIndexOpen, setHuvudgruppIndexOpen] = useState<Array<number>>([]);

  const [isOutsideClick, setIsOutsideClick] = useOutsideClick(focusRef);
  const dropdownIsAbove = useDropdownAdjustment(treeContainer, focusRef, dropdownExpanded);
  const dropdownId = useId();

  useEffect(() => {
    if (isOutsideClick) {
      setDropdownExpanded(false);
      setIsOutsideClick(false);
      setSearchQuery('');
    }
  }, [isOutsideClick, setDropdownExpanded, setIsOutsideClick]);

  useEffect(() => {
    setItems(props.list);
  }, [props.list]);

  useEffect(() => {
    const regex = new RegExp(searchQuery, 'gi');

    if (searchQuery.length >= 3) {
      const searchedList = props.list
        ?.filter((el: HuvudgruppTyp) => {
          // Huvudkategori definitionsnamn.
          const huvudkategoriDefinitionsnamnMatch = el.huvudkategorier.find((huvudkategori: HuvudkategoriTyp) => {
            return huvudkategori.definitionsnamn.match(regex);
          });

          if (huvudkategoriDefinitionsnamnMatch) {
            return true;
          }

          // Huvudkategori nyckelord.
          const huvudkategoriNyckelOrdMatch = el.huvudkategorier.find((huvudkategori: HuvudkategoriTyp) => {
            return huvudkategori.nyckelord.find((nyckelord: NyckelordTyp) => {
              return nyckelord.nyckelord.match(regex);
            });
          });

          if (huvudkategoriNyckelOrdMatch) {
            return true;
          }

          // Underkategori matchning.
          const underkategoriMatch = el.huvudkategorier.find((huvudkategori: HuvudkategoriTyp) => {
            return huvudkategori.underkategorier.find((underkategori: UnderkategoriTyp) => {
              return (
                underkategori.definition.match(regex) ||
                underkategori.nyckelord.find((nyckelord: NyckelordTyp) => {
                  return nyckelord.nyckelord.match(regex);
                })
              );
            });
          });

          if (underkategoriMatch) {
            return true;
          }
        })
        .map((huvudgrupp: HuvudgruppTyp) => {
          return {
            ...huvudgrupp,
            huvudkategorier: huvudgrupp.huvudkategorier.filter((huvudkategori: HuvudkategoriTyp) => {
              // Huvudkategori definitionsnamn
              if (huvudkategori.definitionsnamn.match(regex)) {
                return true;
              }

              // Huvudkategori nyckelord.
              if (
                huvudkategori.nyckelord.find((nyckelord: NyckelordTyp) => {
                  return nyckelord.nyckelord.match(regex);
                })
              ) {
                return true;
              }

              // Underkategori definition & nyckelord
              if (
                huvudkategori.underkategorier.find((underkategori: UnderkategoriTyp) => {
                  return (
                    underkategori.definition.match(regex) ||
                    underkategori.nyckelord.find((nyckelord: NyckelordTyp) => {
                      return nyckelord.nyckelord.match(regex);
                    })
                  );
                })
              ) {
                return true;
              }
            })
          };
        });

      // Expandera sökträffarna i listan.
      if (searchedList?.length) {
        searchedList?.forEach((el, index) => {
          setHuvudgruppIndexOpen((current: Array<number>) => [...current, index]);
          el.huvudkategorier.forEach((huvudkategori, i) => {
            setIndexOpen((current: Array<number>) => [...current, i]);
          });
        });
        setItems(searchedList);
        setDropdownExpanded(true);
      } else {
        setItems([]);
      }
    } else if (searchQuery.length === 0) {
      // Stäng dem öppna sökträffarna när man suddat ut texten.
      setItems(props.list);
      setIndexOpen([]);
      setHuvudgruppIndexOpen([]);
      setDropdownExpanded(false);
    }
  }, [items?.length, props.list, searchQuery]);

  const setFocusOnFirstTreeItem = (): void => {
    const firstTreeItem = treeContainer.current?.firstChild?.firstChild as HTMLElement;
    if (firstTreeItem) {
      firstTreeItem.tabIndex = 0;
      firstTreeItem.focus();
    }
  };

  const handleItemSelection = (selected: HuvudkategoriTyp | UnderkategoriTyp): void => {
    props.onSelect(selected);
    setDropdownExpanded(false);
    setSearchQuery('');
  };

  const findAndSelectItem = (): void => {
    if (items) {
      const huvudkategori = items
        .flatMap((huvudgrupp: HuvudgruppTyp) => huvudgrupp.huvudkategorier)
        .filter((huvudkategori: HuvudkategoriTyp) =>
          huvudkategori.definitionsnamn.toLowerCase().includes(searchQuery.toLowerCase())
        );

      const underkategori = items
        .flatMap((huvudgrupp: HuvudgruppTyp) => huvudgrupp.huvudkategorier)
        .flatMap((huvudkategori: HuvudkategoriTyp) => huvudkategori.underkategorier)
        .filter((underkategori: UnderkategoriTyp) =>
          underkategori.definition.toLowerCase().includes(searchQuery.toLowerCase())
        );

      const result = [...huvudkategori, ...underkategori];

      if (result.length === 1 && !isDisabled(result[0], props.disabledItems)) {
        handleItemSelection(result[0]);
      }
    }
  };

  const handleInputOnKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.altKey || event.ctrlKey || event.metaKey) {
      return;
    }

    if (event.shiftKey && event.code === 'Tab') {
      setDropdownExpanded(false);
      return;
    }

    switch (event.code) {
      case 'ArrowDown':
        setFocusOnFirstTreeItem();
        event.stopPropagation();
        event.preventDefault();
        break;
      case 'Enter':
        findAndSelectItem();
        event.stopPropagation();
        event.preventDefault();
        break;
    }
  };

  const handleButtonOnKeyDown = (event: KeyboardEvent<HTMLButtonElement>): void => {
    if (
      event.altKey ||
      event.ctrlKey ||
      event.metaKey ||
      (event.shiftKey && event.code === 'Tab') ||
      !dropdownExpanded
    ) {
      return;
    }

    switch (event.code) {
      case 'Tab':
      case 'ArrowDown':
        setFocusOnFirstTreeItem();
        event.stopPropagation();
        event.preventDefault();
    }
  };

  return (
    <div ref={focusRef} className={`tw-rounded focus-within:tw-shadow-outline ${props.classNames}`}>
      <div
        className={classNames('tw-flex tw-rounded tw-border tw-border-raa-gray-3', {
          'tw-rounded-t-none': dropdownExpanded && !dropdownIsAbove,
          'tw-rounded-b-none': dropdownExpanded && dropdownIsAbove
        })}
      >
        <input
          autoComplete="off"
          className={classNames(
            'raa-input tw-relative tw-z-0 tw-min-w-0 tw-border-none tw-bg-transparent focus:tw-outline-transparent disabled:tw-bg-raa-gray-2',
            {
              error: props.hasError
            }
          )}
          disabled={props.disabled}
          id={props.inputId}
          role="combobox"
          onChange={(event: FormEvent<HTMLInputElement>) => setSearchQuery((event.target as HTMLInputElement).value)}
          onClick={() => setDropdownExpanded(true)}
          onFocus={() => setDropdownExpanded(true)}
          onKeyDown={handleInputOnKeyDown}
          placeholder={props.placeholder}
          spellCheck="false"
          type="text"
          value={searchQuery}
          aria-expanded={dropdownExpanded}
          aria-controls={dropdownId}
        />
        <button
          disabled={props.disabled}
          aria-label="expandera"
          className="tw-relative tw-flex tw-w-12 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded tw-bg-raa-white tw-p-0 tw-text-raa-orange focus:tw-bg-raa-orange focus:tw-text-raa-white focus:tw-outline-transparent disabled:tw-bg-raa-gray-2 disabled:tw-text-raa-gray-6"
          onClick={() => setDropdownExpanded(!dropdownExpanded)}
          onKeyDown={handleButtonOnKeyDown}
          type="button"
          aria-expanded={dropdownExpanded}
          aria-controls={dropdownId}
        >
          <span
            className={classNames(
              'tw-font-bold raa-icon-arrow-down tw-transform tw-border-none tw-p-0 tw-text-size-1.625',
              {
                'tw-rotate-180': dropdownExpanded
              }
            )}
          />
        </button>
      </div>

      {items && items.length > 0 && dropdownExpanded && (
        <div className="z-index-1 tw-relative" id={dropdownId}>
          <div
            className="tw-absolute tw-left-0 tw-top-0 tw-z-50 tw-mt-[-1px] tw-box-border tw-max-h-[30rem] tw-w-full tw-overflow-y-auto tw-border tw-border-raa-gray-3 tw-bg-raa-white"
            ref={treeContainer}
          >
            <TreeRoot
              disabledItems={props.disabledItems}
              items={items}
              onBlur={() => setDropdownExpanded(false)}
              onItemSelected={handleItemSelection}
              openIndexHandlers={{ indexOpen, huvudgruppIndexOpen }}
              role="tree"
              treeContainer={treeContainer}
            />
          </div>
        </div>
      )}
      {items?.length === 0 && searchQuery.length > 0 && (
        <div className="z-index-1 tw-relative">
          <div
            className="tw-absolute tw-left-0 tw-top-0 tw-z-50 tw-mt-[-1px] tw-box-border tw-max-h-[30rem] tw-w-full tw-overflow-y-auto tw-border tw-border-raa-gray-3 tw-bg-raa-white"
            ref={treeContainer}
          >
            <p className="tw-flex tw-flex-col tw-p-2 tw-outline-transparent">Inga val tillgängliga</p>
          </div>
        </div>
      )}
    </div>
  );
};

CategorySelect.defaultProps = {
  classNames: '',
  disabled: false,
  placeholder: ''
};

export default CategorySelect;
